import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Share2, UserPlus, UserCheck, Bell, Loader2, User, Mail, Check, X, Coins } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { optimizeImg } from "@/lib/img";
import BottomNav from "@/components/BottomNav";

const ICON = { like: Heart, comment: MessageCircle, share: Share2, follow: UserPlus, follow_accept: UserCheck, message: Mail, coins: Coins };
const TEXT = {
  like: "liked your post", comment: "commented on your post", share: "shared your post",
  follow: "started following you", follow_accept: "accepted your follow request", message: "sent you a message",
};

export default function Notifications() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("activity");
  const [list, setList] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [convs, setConvs] = useState(null);

  const loadReqs = useCallback(() => {
    api.get("/follow-requests").then(({ data }) => setReqs(data)).catch(() => setReqs([]));
  }, []);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setList(data.notifications)).catch(() => setList([]));
    api.post("/notifications/read").catch(() => {});
    loadReqs();
  }, [loadReqs]);

  useEffect(() => {
    if (tab === "messages" && convs === null) {
      api.get("/conversations").then(({ data }) => setConvs(data.conversations)).catch(() => setConvs([]));
    }
  }, [tab, convs]);

  const act = async (id, decision) => {
    try {
      await api.post(`/follow-requests/${id}/${decision}`);
      setReqs((r) => r.filter((x) => x.id !== id));
      toast.success(decision === "accept" ? "Request accepted" : "Request declined");
    } catch { toast.error("Couldn't update request"); }
  };

  const timeAgo = (iso) => {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "now"; if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`; return `${Math.floor(s / 86400)}d`;
  };

  return (
    <div className="min-h-screen bg-ink-950 text-[#F0E9DD] pb-24">
      <header className="sticky top-0 z-30 bg-ink-950/85 backdrop-blur-xl border-b border-white/10 px-4 pt-5 pb-0">
        <h1 className="font-heading font-black uppercase tracking-tight text-2xl mb-3">Inbox</h1>
        <div className="flex gap-6">
          {[{ v: "activity", l: "Notifications" }, { v: "messages", l: "Messages" }].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`inbox-tab-${t.v}`}
              className={`relative pb-3 font-heading font-bold text-sm ${tab === t.v ? "text-gold" : "text-zinc-500"}`}>
              {t.l}{tab === t.v && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-gold" />}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 pt-4">
        {tab === "messages" ? (
          convs === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" size={26} /></div>
          : convs.length === 0 ? (
            <div className="border border-dashed border-zinc-800 rounded-xl py-20 flex flex-col items-center text-center" data-testid="messages-placeholder">
              <Mail size={36} className="text-zinc-700 mb-3" />
              <p className="text-zinc-400">No messages yet.</p>
              <p className="text-xs text-zinc-600 mt-1">Tap Message on any profile to start a chat.</p>
            </div>
          ) : (
            <div className="space-y-1" data-testid="notif-conversations">
              {convs.map((c) => (
                <button key={c.id} onClick={() => navigate(`/messages?c=${c.id}`)} data-testid={`notif-conv-${c.other.id}`}
                  className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-ink-900">
                  <div className="w-11 h-11 rounded-full overflow-hidden bg-ink-800 shrink-0">
                    {c.other.avatar_url ? <img src={optimizeImg(c.other.avatar_url, 96, 70)} alt="" className="w-full h-full object-cover" /> : <User size={18} className="m-auto mt-3 text-zinc-500" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold truncate">{c.other.username || c.other.name}</div>
                    <div className="text-sm text-zinc-500 truncate">{c.last_message || "New conversation"}</div>
                  </div>
                  {c.unread > 0 && <span className="w-5 h-5 rounded-full bg-gold text-ink-950 text-[10px] font-bold flex items-center justify-center shrink-0">{c.unread}</span>}
                </button>
              ))}
            </div>
          )
        ) : (
          <>
            {reqs.length > 0 && (
              <div className="mb-4" data-testid="follow-requests">
                <h2 className="font-heading font-bold text-sm text-zinc-400 mb-2">Follow requests</h2>
                <div className="space-y-1.5">
                  {reqs.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5 bg-[#1a140d] border border-gold/20" data-testid={`follow-request-${r.requester.id}`}>
                      <button onClick={() => navigate(`/u/${r.requester.id}`)} className="w-9 h-9 rounded-full overflow-hidden bg-ink-800 shrink-0">
                        {r.requester.avatar_url ? <img src={optimizeImg(r.requester.avatar_url, 80, 70)} alt="" className="w-full h-full object-cover" /> : <User size={16} className="m-auto mt-2.5 text-zinc-500" />}</button>
                      <div className="flex-1 min-w-0 text-sm"><b>{r.requester.username || r.requester.name}</b> <span className="text-zinc-400">requested to follow you</span></div>
                      <button onClick={() => act(r.id, "accept")} data-testid={`accept-request-${r.requester.id}`} className="w-8 h-8 rounded-full bg-gold text-ink-950 flex items-center justify-center shrink-0"><Check size={16} /></button>
                      <button onClick={() => act(r.id, "decline")} data-testid={`decline-request-${r.requester.id}`} className="w-8 h-8 rounded-full border border-zinc-700 text-zinc-300 flex items-center justify-center shrink-0"><X size={16} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {list === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" size={26} /></div>
              : (list.filter((n) => n.kind !== "follow_request").length === 0 && reqs.length === 0) ? (
                <div className="border border-dashed border-zinc-800 rounded-xl py-20 flex flex-col items-center text-center" data-testid="notifs-empty">
                  <Bell size={36} className="text-zinc-700 mb-3" /><p className="text-zinc-400">No notifications yet.</p>
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="notifs-list">
                  {list.filter((n) => n.kind !== "follow_request").map((n) => {
                    const Icon = ICON[n.kind] || Bell;
                    const go = () => n.kind === "message" ? navigate(`/messages?to=${n.actor_id}`) : n.post_id ? navigate("/home") : navigate(`/u/${n.actor_id}`);
                    return (
                      <button key={n.id} onClick={go}
                        className={`w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left ${n.read ? "" : "bg-[#1a140d]"}`}>
                        <div className="w-9 h-9 rounded-full overflow-hidden bg-ink-800 shrink-0">{n.actor_avatar ? <img src={n.actor_avatar} className="w-full h-full object-cover" alt="" /> : <User size={16} className="m-auto mt-2.5 text-zinc-500" />}</div>
                        <div className="flex-1 min-w-0 text-sm"><b>{n.actor_name}</b> <span className="text-zinc-400">{TEXT[n.kind] || "interacted"}</span>{n.text && <span className="text-zinc-500"> — {n.text}</span>}<div className="text-[10px] text-zinc-600">{timeAgo(n.created_at)}</div></div>
                        <Icon size={16} className="text-gold shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
